import { getClassLevel } from './classIdentity.mjs';
import { SUPERIOR_DEFENSE, superiorDefenseActive, superiorDefenseResists } from './effects/superiorDefense.mjs';

export const getMonkLevel = character => getClassLevel(character, 'monk');
// 2024 Monk features from level 6 on (SRD 5.2), by the Monk level that grants them.
export const MONK_FEATURE_LEVELS = Object.freeze({ empoweredStrikes: 6, evasion: 7, acrobaticMovement: 9, heightenedFocus: 10, selfRestoration: 10,
  deflectEnergy: 13, disciplinedSurvivor: 14, perfectFocus: 15, superiorDefense: 18, bodyAndMind: 20 });
export const hasMonkFeature = (character, feature) => getMonkLevel(character) >= (MONK_FEATURE_LEVELS[feature] ?? Infinity);
/**
 * Empowered Strikes (6): an Unarmed Strike can deal Force damage or its normal type. The app takes the type the target
 * loses more HP to (`appliedFor(type)`), keeping the normal type on a tie.
 */
export const empoweredStrikesType = (baseType, appliedFor) => (appliedFor('force') > appliedFor(baseType) ? 'force' : baseType);
/**
 * Self-Restoration (10): at the end of each of its turns a Monk ends one of Charmed, Frightened or Poisoned on itself.
 * With two or more on it, the player chooses which during the turn (`SELF_RESTORATION_CHOICE`, a turn state naming the
 * condition); without a choice the app takes the one that hinders a Monk most: Poisoned (Disadvantage on attacks and
 * checks), then Frightened, then Charmed.
 */
export const SELF_RESTORATION_CONDITIONS = Object.freeze(['condition:poisoned', 'condition:frightened', 'condition:charmed']);
export const SELF_RESTORATION_CHOICE = 'monk:self-restoration-choice';
/** The conditions Self-Restoration could end on this creature now, in the default order. */
export const selfRestorationConditions = (activeEffects, combatantId) => SELF_RESTORATION_CONDITIONS.filter(definitionId => (activeEffects || [])
  .some(effect => effect?.definitionId === definitionId && String(effect.targetCombatantId) === String(combatantId)));
/** The condition chosen this turn (from the turn ledger), if it is still on the creature. */
export const selfRestorationChoice = (combat, combatantId) => {
  const ledger = combat?.turnActionLedger;
  if (ledger?.combatantId !== String(combatantId) || ledger?.turnSequence !== combat?.turnSequence) return null;
  const choice = [...(ledger.turnStates || [])].reverse().find(state => state.definitionId === SELF_RESTORATION_CHOICE)?.conditionId;
  return choice && selfRestorationConditions(combat.activeEffects, combatantId).includes(choice) ? choice : null;
};
/** The condition that ends at the end of the creature's turn: the chosen one, else the default order; or null. */
export const selfRestorationCondition = (combat, combatantId) => selfRestorationChoice(combat, combatantId)
  || selfRestorationConditions(combat?.activeEffects, combatantId)[0] || null;
/** An effect of the condition that would end (for display), or null. */
export const selfRestorationEffect = (activeEffects, combatantId, conditionId = null) => {
  const definitionId = conditionId || selfRestorationConditions(activeEffects, combatantId)[0];
  return (activeEffects || []).find(effect => effect?.definitionId === definitionId && String(effect.targetCombatantId) === String(combatantId)) || null;
};
export const getMonkFocusPoints = character => getMonkLevel(character) >= 2 ? getMonkLevel(character) : 0;
export const martialArtsDie = character => {
  const level = getMonkLevel(character);
  return level < 1 ? 0 : level < 5 ? 6 : level < 11 ? 8 : level < 17 ? 10 : 12;
};
const text = item => String([item?.source, item?.__source, item?.category, item?.type, item?.armorType, item?.name, item?.armorName].filter(Boolean).join(' ')).toLowerCase();
export const isUnarmedStrike = weapon => String(weapon?.name ?? '').trim().toLowerCase() === 'unarmed strike' || /unarmed/i.test(String(weapon?.type ?? weapon?.category ?? ''));
export const isMonkWeapon = weapon => {
  const category = String(weapon?.category ?? weapon?.type ?? '').toLowerCase();
  const properties = Array.isArray(weapon?.properties) ? weapon.properties : typeof weapon?.properties === 'string' ? weapon.properties.split(/[,;]/) : [];
  const light = properties.some(property => /^light(?:\s|$)/i.test(String(property?.name ?? property).trim()));
  const base = String(weapon?.type ?? weapon?.name ?? '').trim().toLowerCase();
  const simpleMelee = ['club','dagger','greatclub','handaxe','javelin','light hammer','mace','quarterstaff','sickle','spear'];
  const classified = category.includes('melee') && (category.includes('simple') || (category.includes('martial') && light));
  const fallback = !category.includes('melee') && !category.includes('ranged') && (simpleMelee.includes(base) || ['shortsword','scimitar'].includes(base));
  return classified || fallback;
};
const equipped = character => Object.values(character?.equipment || {}).filter(item => item && item.owned !== false);
export const isMonkUnarmored = character => !equipped(character).some(item => {
  if (Array.isArray(item)) {
    const [name, ac, maxDex, category] = item;
    return !/no armor|unarmored|none/i.test(String(name)) && (/shield|armor|light|medium|heavy/i.test(`${name || ''} ${category || ''}`) || Number(ac) > 0 || Number(maxDex) > 0);
  }
  const value = text(item);
  if (/no armor|unarmored|none/.test(value)) return false;
  const source = String(item.source ?? item.__source ?? '').toLowerCase();
  const category = String(item.category ?? item.type ?? item.armorType ?? '').toLowerCase();
  return source === 'armor' || /shield|armor/.test(category) || ['light','medium','heavy'].includes(category)
    || (!source && !category && /\bshield\b|\barmor\b/.test(value));
});
export const canUseMartialArts = character => getMonkLevel(character) > 0 && isMonkUnarmored(character)
  && ['mainHand','offHand'].every(slot => {
    const item = character?.equipment?.[slot];
    return !item || item.owned === false || isUnarmedStrike(item) || isMonkWeapon(item);
  });
export const monkMovementBonus = character => {
  const level = getMonkLevel(character);
  return level < 2 || !isMonkUnarmored(character) ? 0 : level < 6 ? 10 : level < 10 ? 15 : level < 14 ? 20 : level < 18 ? 25 : 30;
};
export const monkDamage = (character, weapon, damage) => {
  if (!canUseMartialArts(character) || !(isUnarmedStrike(weapon) || isMonkWeapon(weapon))) return damage;
  const sides = martialArtsDie(character);
  const match = /\b(\d+)d(\d+)\b/i.exec(damage);
  if (match && Number(match[1]) * (Number(match[2]) + 1) >= sides + 1) return damage;
  return match ? damage.replace(match[0], `1d${sides}`) : damage.replace(/^1\b/, `1d${sides}`);
};
export const focusProfile = character => {
  const max = getMonkFocusPoints(character);
  const stored = character?.monkFocus || {};
  const spent = Math.min(max, Math.max(0, Math.trunc(Number(stored.spent) || 0)));
  return { ...(stored.restRevision!==undefined?{restRevision:stored.restRevision}:{}), ...(stored.metabolismUsed!==undefined?{metabolismUsed:stored.metabolismUsed}:{}), max, spent, remaining: max - spent, revision: Math.max(0, Math.trunc(Number(stored.revision) || 0)) };
};
/**
 * Perfect Focus (15): rolling Initiative without using Uncanny Metabolism tops Focus up to 4 when 3 or fewer remain.
 * Returns the next `monkFocus` record, or null when nothing changes.
 */
export const perfectFocusRestore = character => {
  if (!hasMonkFeature(character, 'perfectFocus')) return null;
  const profile = focusProfile(character);
  if (profile.remaining > 3) return null;
  return { ...(character.monkFocus || {}), spent: Math.max(0, profile.max - 4), revision: profile.revision + 1 };
};
/**
 * Superior Defense (18): at the start of its turn (before it does anything) a Monk can spend 3 Focus Points for 1 minute
 * of Resistance to all damage except Force. It ends early when the Monk has the Incapacitated condition.
 */
export { SUPERIOR_DEFENSE, superiorDefenseActive, superiorDefenseResists };
export const SUPERIOR_DEFENSE_COST = 3;
export const turnUntouched = ledger => !ledger || (!ledger.actionSpent && !ledger.bonusActionSpent && !(Number(ledger.movement?.spentFeet) > 0) && !Object.keys(ledger.requests || {}).length);
export const canStartSuperiorDefense = (character, combat, id, ledger) => hasMonkFeature(character, 'superiorDefense') && focusProfile(character).remaining >= SUPERIOR_DEFENSE_COST
  && !superiorDefenseActive(combat, id) && turnUntouched(ledger);
export const superiorDefenseEffect = ({ id, combatantId, turnSequence }) => ({
  id: `superior-defense:${id}`, definitionId: SUPERIOR_DEFENSE, name: 'Superior Defense', icon: '🛡', userVisible: true,
  sourceCombatantId: combatantId, targetCombatantId: combatantId, sourceFeature: SUPERIOR_DEFENSE, appliedAtTurnSequence: turnSequence,
  expiration: { type: 'sourceTurn', combatantId, boundary: 'start', remainingOccurrences: 10 },
  description: 'Resistance to all damage except Force for 1 minute. Ends if you have the Incapacitated condition.',
});
export const mergeMonkFocus = (character, focus) => !focus || Number(focus.revision) < Number(character?.monkFocus?.revision || 0)
  ? character : { ...character, monkFocus: focus };
